import { useState } from 'react';
import { c, rgbaOf, tone } from '../theme';
import type { ToneKey } from '../api';

/**
 * The overview's module list in five areas, from the Modules handoff. Each
 * card opens and closes; closed, it still shows one dot per module in that
 * module's rating colour and a digest of what needs watching. Open state is
 * remembered per report.
 */

type Mod = { key: string; title: string; sub: string; tag: string; tone: ToneKey };

export const GROUPS: { id: string; title: string; tone: string; keys: string[] }[] = [
  { id: 'deal', title: 'Your Deal', tone: c.red, keys: ['your-position', 'offer-window', 'agent-intel', 'buyer-fit', 'supply-demand'] },
  { id: 'property', title: 'The Property', tone: c.violet, keys: ['property-profile', 'planning-zoning', 'risks', 'development'] },
  {
    id: 'money',
    title: 'Price & Money',
    tone: c.cyan,
    keys: ['street-forecast', 'suburb-benchmark', 'market-signals', 'growth-drivers', 'budget-exposure', 'rental-signals', 'properties-nearby'],
  },
  { id: 'neighbourhood', title: 'The Neighbourhood', tone: tone.strong, keys: ['lifestyle', 'schools', 'crime'] },
  { id: 'people', title: 'The People', tone: tone.pink, keys: ['demographics', 'ethnicity', 'similar-suburbs'] },
];

const MUTED = 'rgba(31,26,31,0.6)';

/** What a module dot's colour means. Areas use the three brand dots instead, so they never read as a rating. */
const LEGEND: { label: string; tones: ToneKey[] }[] = [
  { label: 'Strong', tones: ['strong'] },
  { label: 'Fair', tones: ['ok', 'cyan'] },
  { label: 'Watch', tones: ['warn'] },
  { label: 'Concern', tones: ['high'] },
  { label: 'For information', tones: ['neutral'] },
];
// Cyan and pink are decorative tones, not ratings: their dots read as Fair, so the key stays true.
const solid = (t: ToneKey) => (t === 'cyan' || t === 'pink' ? tone.ok : tone[t].startsWith('#') ? tone[t] : '#1F1A1F');

export function ModuleGroups({ reportId, restore, modules, onOpen }: { reportId?: string; restore?: boolean; modules: Mod[]; onOpen: (key: string) => void }) {
  // Every area starts closed on a fresh visit; coming back from a module, the areas that were open stay open.
  const KEY = `rb-ov-open-${reportId ?? ''}`;
  const [open, setOpen] = useState<Record<string, boolean>>(() => {
    if (!restore) return {};
    try {
      return JSON.parse(sessionStorage.getItem(KEY) ?? '{}') as Record<string, boolean>;
    } catch {
      return {};
    }
  });
  const save = (next: Record<string, boolean>) => {
    setOpen(next);
    try {
      sessionStorage.setItem(KEY, JSON.stringify(next));
    } catch {
      /* storage blocked */
    }
  };

  // A module no group names (a new one) joins the last area rather than vanishing.
  const placed = new Set(GROUPS.flatMap((g) => g.keys));
  const groups = GROUPS.map((g, i) => ({
    ...g,
    items: [
      ...g.keys.map((k) => modules.find((m) => m.key === k)).filter((m): m is Mod => !!m),
      ...(i === GROUPS.length - 1 ? modules.filter((m) => !placed.has(m.key)) : []),
    ],
  })).filter((g) => g.items.length);
  const allOpen = groups.every((g) => open[g.id]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {/* "22 Modules. 5 areas." — centred, with the expand control as the violet hint pill and a key to the dot colours */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '0 var(--gx)', textAlign: 'center' }}>
        <div style={{ fontSize: 24, whiteSpace: 'nowrap', fontWeight: 500 }}>
          <span style={{ fontWeight: 700 }}>{modules.length}</span> Modules<span style={{ color: c.red }}>.</span>{' '}
          <span style={{ fontWeight: 700 }}>{groups.length}</span> areas<span style={{ color: c.red }}>.</span>
        </div>
        <button
          type="button"
          onClick={() => save(Object.fromEntries(groups.map((g) => [g.id, !allOpen])))}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '9px 16px 9px 12px',
            borderRadius: 999,
            background: 'rgba(106,43,217,0.1)',
            border: '1px solid rgba(106,43,217,0.35)',
            color: c.violet,
            fontSize: 12,
            fontWeight: 600,
            whiteSpace: 'nowrap',
          }}
        >
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: c.violet, animation: 'throb 2.4s ease-in-out infinite' }} />
          {allOpen ? 'Tap to collapse all' : 'Tap an area to expand'}
        </button>
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '6px 14px', fontSize: 11, color: MUTED }} aria-label="Module rating colours">
          {LEGEND.filter((l) => modules.some((m) => l.tones.includes(m.tone))).map((l) => (
            <span key={l.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: solid(l.tones[0]) }} />
              {l.label}
            </span>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '26px 24px 0' }}>
        {groups.map((g) => {
          const isOpen = !!open[g.id];
          const watch = g.items.filter((m) => m.tone === 'high' || m.tone === 'warn').length;
          const good = g.items.filter((m) => m.tone === 'strong').length;
          const counts = [watch ? `${watch} to watch` : '', good ? `${good} strong` : ''].filter(Boolean);
          const digest = counts.length
            ? `${counts.join(' · ')} · ${g.items.slice(0, 2).map((m) => m.title).join(', ')}`
            : g.items.slice(0, 3).map((m) => m.title).join(', ');
          return (
            <div key={g.id} style={{ borderRadius: 30, background: isOpen ? c.tintOpen : c.tint, boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.6)', overflow: 'hidden' }}>
              <button
                type="button"
                onClick={() => save({ ...open, [g.id]: !isOpen })}
                aria-expanded={isOpen}
                style={{ width: '100%', display: 'grid', gridTemplateColumns: '30px 1fr auto', alignItems: 'center', gap: 14, padding: '18px 18px 18px 16px', color: c.ink, textAlign: 'left' }}
              >
                <span aria-hidden style={{ width: 30, height: 30, borderRadius: '50%', border: `1px solid ${c.ink18}`, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2.5 }}>
                  {[c.red, c.violet, c.cyan].map((d) => (
                    <span key={d} style={{ width: 4, height: 4, borderRadius: '50%', background: d }} />
                  ))}
                </span>
                <span style={{ display: 'flex', flexDirection: 'column', gap: 7, minWidth: 0 }}>
                  <span style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                    <span style={{ fontSize: 16, fontWeight: 700, letterSpacing: '-0.01em', whiteSpace: 'nowrap' }}>{g.title}</span>
                    <span style={{ fontSize: 9, letterSpacing: '0.14em', fontWeight: 600, color: 'rgba(31,26,31,0.5)', whiteSpace: 'nowrap' }}>
                      {g.items.length} {g.items.length === 1 ? 'MODULE' : 'MODULES'}
                    </span>
                  </span>
                  <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }} aria-hidden>
                    {g.items.map((m) => (
                      <span key={m.key} style={{ width: 7, height: 7, borderRadius: '50%', background: solid(m.tone) }} />
                    ))}
                  </span>
                  <span style={{ fontSize: 11, lineHeight: 1.4, color: MUTED }}>{digest}</span>
                </span>
                <span
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    border: '1px solid rgba(31,26,31,0.18)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transform: `rotate(${isOpen ? 180 : 0}deg)`,
                    transition: 'transform .2s',
                  }}
                >
                  <svg width="12" height="12" viewBox="0 0 14 14" fill="none" aria-hidden>
                    <path d="M3 5.5L7 9.5l4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
              </button>
              {isOpen && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '2px 10px 12px' }}>
                  {g.items.map((m) => {
                    const fg = solid(m.tone);
                    return (
                      <button
                        key={m.key}
                        type="button"
                        className="rb-tile"
                        onClick={() => onOpen(m.key)}
                        style={{ display: 'grid', gridTemplateColumns: '26px 1fr auto', alignItems: 'center', gap: 14, padding: '14px 12px', borderRadius: 20, background: c.tint, color: c.ink, textAlign: 'left' }}
                      >
                        <span style={{ width: 26, height: 26, borderRadius: '50%', background: rgbaOf(fg, 0.16), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <span style={{ width: 6, height: 6, borderRadius: '50%', background: fg }} />
                        </span>
                        <span style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
                          <span style={{ fontSize: 14, fontWeight: 600 }}>{m.title}</span>
                          <span style={{ fontSize: 11, color: 'rgba(31,26,31,0.55)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.sub}</span>
                        </span>
                        <span
                          style={{
                            fontSize: 8,
                            letterSpacing: '0.1em',
                            fontWeight: 700,
                            color: tone[m.tone],
                            textAlign: 'center',
                            maxWidth: 84,
                            lineHeight: 1.3,
                            padding: '5px 8px',
                            borderRadius: 9,
                            border: `1.5px solid ${rgbaOf(fg, 0.16)}`,
                          }}
                        >
                          {m.tag}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
